import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getCustomSet, getCustomSetItems, updateCustomSet, 
  removeContentFromCustomSet, removeLessonFromCustomSet,
  CustomSetWithContent, CustomSetItems 
} from '../services/api';
import './CustomSetEdit.css';

const CustomSetEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [set, setSet] = useState<CustomSetWithContent | null>(null);
  const [items, setItems] = useState<CustomSetItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      const [setData, itemsData] = await Promise.all([
        getCustomSet(id),
        getCustomSetItems(id)
      ]);
      setSet(setData);
      setItems(itemsData);
      setEditName(setData.name);
    } catch (err) {
      console.error('Failed to load set:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!id || !editName.trim()) return;
    try {
      await updateCustomSet(id, { name: editName.trim() });
      setEditing(false);
      loadData();
    } catch (err) {
      console.error('Failed to update name:', err);
    }
  };

  const handleRemoveContent = async (contentId: string) => {
    if (!id) return;
    try {
      await removeContentFromCustomSet(id, contentId);
      loadData();
    } catch (err) {
      console.error('Failed to remove content:', err);
    }
  };

  const handleRemoveLesson = async (lessonId: string) => {
    if (!id) return;
    try {
      await removeLessonFromCustomSet(id, lessonId);
      loadData();
    } catch (err) {
      console.error('Failed to remove lesson:', err);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (!set) {
    return (
      <div className="error-page">
        <h2>Nie znaleziono zestawu</h2>
        <Link to="/custom-sets" className="btn btn-primary">Wróć do zestawów</Link>
      </div>
    );
  }

  return (
    <div className="custom-set-edit fade-in">
      <div className="set-header-edit" style={{ '--set-color': set.color } as React.CSSProperties}>
        <span className="set-icon-large">{set.icon}</span>
        {editing ? (
          <div className="edit-name-form">
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              autoFocus
            />
            <button className="btn btn-primary btn-small" onClick={handleSaveName}>✓</button>
            <button className="btn btn-secondary btn-small" onClick={() => setEditing(false)}>✕</button>
          </div>
        ) : (
          <h1 onClick={() => setEditing(true)}>{set.name} ✏️</h1>
        )}
      </div>

      <div className="set-stats-bar">
        <span>📝 {set.word_count} słów</span>
        <span>💬 {set.sentence_count} zdań</span>
        <span>📚 {items?.lessons.length || 0} lekcji</span>
      </div>

      <div className="action-buttons">
        <Link to={`/custom-sets/${id}/learn`} className="btn btn-primary">
          📖 Ucz się
        </Link>
        <Link to={`/custom-sets/${id}/add`} className="btn btn-secondary">
          ➕ Dodaj słówka
        </Link>
      </div>

      {items && items.lessons.length > 0 && (
        <div className="items-section">
          <h2>📚 Lekcje w zestawie</h2>
          <div className="items-list">
            {items.lessons.map(lesson => (
              <div key={lesson.item_id} className="item-card lesson-item">
                <div className="item-info">
                  <strong>{lesson.name_pl}</strong>
                  <span className="item-meta">{lesson.content_count} elementów</span>
                </div>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => handleRemoveLesson(lesson.id)}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items && items.content.length > 0 && (
        <div className="items-section">
          <h2>📝 Pojedyncze słówka</h2>
          <div className="items-list">
            {items.content.map(item => (
              <div key={item.item_id} className="item-card content-item">
                <div className="item-info">
                  <strong>{item.english}</strong>
                  <span className="item-translation">{item.polish}</span>
                  <span className="item-meta">{item.lesson_name}</span>
                </div>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => handleRemoveContent(item.id)}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!items || (items.lessons.length === 0 && items.content.length === 0)) && (
        <div className="empty-set">
          <div className="empty-icon">📭</div>
          <h3>Ten zestaw jest pusty</h3>
          <p>Dodaj słówka lub całe lekcje, aby rozpocząć naukę!</p>
          <Link to={`/custom-sets/${id}/add`} className="btn btn-primary">
            ➕ Dodaj słówka
          </Link>
        </div>
      )}
    </div>
  );
};

export default CustomSetEdit;

